import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';

// Text that reveals character by character
export const AnimatedText = ({ 
  text, 
  className = '', 
  delay = 0, 
  staggerDelay = 0.03,
  once = true 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin: "-100px" });
  
  const characters = text.split('');

  return (
    <span ref={ref} className={className}>
      {characters.map((char, index) => (
        <motion.span
          key={index}
          initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
          animate={isInView ? { 
            opacity: 1, 
            y: 0, 
            filter: 'blur(0px)' 
          } : {}}
          transition={{
            duration: 0.5,
            delay: delay + index * staggerDelay,
            ease: [0.16, 1, 0.3, 1],
          }}
          style={{ display: 'inline-block', whiteSpace: char === ' ' ? 'pre' : 'normal' }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </span>
  );
};

// Text that slides up from bottom
export const SlideUpText = ({ 
  children, 
  className = '', 
  delay = 0, 
  once = true 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.8,
        delay,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Text with line reveal effect
export const LineRevealText = ({ 
  children, 
  className = '', 
  delay = 0,
  once = true 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin: "-50px" });

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: '100%' }}
        animate={isInView ? { y: 0 } : {}}
        transition={{
          duration: 0.8,
          delay,
          ease: [0.77, 0, 0.175, 1],
        }}
      >
        {children}
      </motion.div>
    </div>
  );
};

// Gold underline that draws itself
export const GoldUnderline = ({ 
  className = '', 
  delay = 0,
  once = true 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      className={`h-[1px] bg-[#C5A55C] ${className}`}
      initial={{ scaleX: 0 }}
      animate={isInView ? { scaleX: 1 } : {}}
      transition={{
        duration: 1,
        delay,
        ease: [0.77, 0, 0.175, 1],
      }}
      style={{ transformOrigin: 'left' }}
    />
  );
};

// Staggered words animation
export const StaggeredWords = ({ 
  text, 
  className = '', 
  wordClassName = '',
  delay = 0,
  staggerDelay = 0.1,
  once = true 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin: "-50px" });
  
  const words = text.split(' ');

  return (
    <span ref={ref} className={className}>
      {words.map((word, index) => (
        <span key={index} className="overflow-hidden inline-block">
          <motion.span
            className={`inline-block ${wordClassName}`}
            initial={{ y: '100%', opacity: 0 }}
            animate={isInView ? { y: 0, opacity: 1 } : {}}
            transition={{
              duration: 0.6,
              delay: delay + index * staggerDelay,
              ease: [0.77, 0, 0.175, 1],
            }}
          >
            {word}
          </motion.span>
          {index < words.length - 1 && '\u00A0'}
        </span>
      ))}
    </span>
  );
};

// Number counter animation
export const AnimatedCounter = ({ 
  value, 
  suffix = '', 
  className = '',
  duration = 2,
  delay = 0,
  once = true 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin: "-50px" });
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    const numValue = parseInt(value.replace(/\D/g, ''));
    const startTime = Date.now();
    const delayMs = delay * 1000;

    const timer = setTimeout(() => {
      const animate = () => {
        const elapsed = Date.now() - startTime - delayMs;
        const progress = Math.min(elapsed / (duration * 1000), 1);
        
        // Ease out cubic
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.round(easeProgress * numValue);
        
        setDisplayValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      animate();
    }, delayMs);

    return () => clearTimeout(timer);
  }, [isInView, value, duration, delay]);

  return (
    <span ref={ref} className={className}>
      {displayValue}{suffix}
    </span>
  );
};

export default {
  AnimatedText,
  SlideUpText,
  LineRevealText,
  GoldUnderline,
  StaggeredWords,
  AnimatedCounter,
};
